import React, { useState, useEffect } from 'react';
import { Stack, Paper, Title, Text, TextInput, Textarea, Button, Group, Loader, Center, Alert, Code, CopyButton, ActionIcon, Tooltip, Tabs } from '@mantine/core';
import { IconPhone, IconDeviceFloppy, IconInfoCircle, IconCopy, IconCheck, IconSettings, IconListDetails } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export default function PhoneCallsTab({ user, API_BASE_URL }) {
  const { t } = useTranslation();
  const [config, setConfig] = useState({ 'twilio.phone.number': '', 'openai.api.key.voice': '' });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    setIsLoading(true);
    fetch(`${API_BASE_URL}/api/config?ownerEmail=${encodeURIComponent(user.email)}`)
      .then(res => res.json())
      .then(data => {
        setConfig({
          'twilio.phone.number': data['twilio.phone.number'] || '',
          'openai.api.key.voice': data['openai.api.key.voice'] || '',
          'ai.voice.system.prompt': data['ai.voice.system.prompt'] || ''
        });
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Failed to fetch configs", err);
        setIsLoading(false);
      });
  }, [user, API_BASE_URL]);

  const handleSave = () => {
    setIsSaving(true);
    fetch(`${API_BASE_URL}/api/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...config, ownerEmail: user.email })
    })
      .then(async (res) => {
        if (res.ok) {
          alert(t('save_success_desc', 'Ustawienia zostały zapisane pomyślnie!'));
        } else {
          alert(t('save_error_desc', 'Wystąpił błąd podczas zapisywania.'));
        }
        setIsSaving(false);
      })
      .catch(err => {
        console.error(err);
        setIsSaving(false);
        alert(t('save_error_desc', 'Wystąpił błąd podczas zapisywania.'));
      });
  };

  return (
    <Stack gap="xl" p="md">
          <Paper withBorder p="xl" radius="md">
            <Title order={3} mb="sm" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IconPhone size={24} /> {t('calls_title', 'Phone Calls & AI Voice Settings')}
            </Title>
            <Text c="dimmed" mb="sm">
              {t('calls_description', 'Skonfiguruj numer telefonu Twilio oraz klucz API OpenAI, aby włączyć funkcję asystenta głosowego.')}
            </Text>
            
            <Alert variant="light" color="teal" title={t('calls_instruction_title', 'Instrukcja Konfiguracji')} icon={<IconInfoCircle />} mb="xl">
              <Text size="sm">
                {t('calls_instruction_desc', 'Nie wiesz jak założyć i skonfigurować konto w OpenAI oraz jak wykupić numer w Twilio? Pobierz naszą instrukcję krok po kroku: ')}
                <a href="/Voice_Setup_Instructions.pdf" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 'bold', color: 'inherit', textDecoration: 'underline' }}>
                  {t('calls_instruction_link', 'Pobierz Instrukcję (PDF)')}
                </a>
              </Text>
            </Alert>
            
            <Stack gap="md" maw={800}>
              <TextInput
                label={t('twilio_phone_number', 'Twilio Phone Number')}
                placeholder="+48123456789"
                value={config['twilio.phone.number']}
                onChange={(e) => setConfig({ ...config, 'twilio.phone.number': e.target.value })}
              />
              <TextInput
                label={t('openai_voice_api_key', 'OpenAI API Key (Realtime Voice)')}
                placeholder="sk-proj-..."
                type="password"
                value={config['openai.api.key.voice']}
                onChange={(e) => setConfig({ ...config, 'openai.api.key.voice': e.target.value })}
              />
              <Textarea
                label={t('ai_voice_system_prompt_label', 'Instrukcje dla Agenta Głosowego (Voice Prompt)')}
                description={t('ai_voice_system_prompt_desc', 'Zostaw puste, aby użyć domyślnego zachowania (asystent zbiera dane dzwoniącego i powód kontaktu), lub wpisz własne zasady prowadzenia rozmowy.')}
                placeholder={t('ai_voice_system_prompt_placeholder', 'Domyślnie: Jesteś asystentem głosowym...\n(Opcjonalnie) Jeśli dzwoni numer 00000 lub 11111, omiń agenta i połącz bezpośrednio z odbiorcą.')}
                minRows={30}
                maxRows={50}
                autosize
                value={config['ai.voice.system.prompt']}
                onChange={(e) => setConfig({ ...config, 'ai.voice.system.prompt': e.target.value })}
              />
              <Button mt="md" onClick={handleSave} loading={isSaving} leftSection={<IconDeviceFloppy size={16} />}>
                  {t('btn_save_config', 'Save Voice Settings')}
              </Button>
            </Stack>

            <Alert variant="light" color="blue" title={t('webhook_config_title', 'Konfiguracja Webhooka Twilio')} icon={<IconInfoCircle />} mt="xl">
              <Text size="sm" mb="md">
                {t('webhook_config_desc', 'Skopiuj poniższy adres URL i wklej go w ustawieniach numeru na platformie Twilio:')} 
              </Text>
              <Group>
                <TextInput 
                  readOnly 
                  value={`https://ailinkb.shop/api/voice/twilio/incoming?ownerEmail=${encodeURIComponent(user?.email || '')}`} 
                  style={{ flex: 1 }}
                />
                <Tooltip label={copied ? t('btn_copied', 'Skopiowano!') : t('btn_copy', 'Kopiuj')} withArrow position="right">
                  <ActionIcon variant="light" color={copied ? 'teal' : 'blue'} size="lg" onClick={() => {
                    navigator.clipboard.writeText(`https://ailinkb.shop/api/voice/twilio/incoming?ownerEmail=${encodeURIComponent(user?.email || '')}`);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}>
                    {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Alert>
          </Paper>
    </Stack>
  );
}
